import { timeOfDay } from '@shefira/shared';
import Phaser from 'phaser';

import { followPreference } from '../settings/preferences.js';
import { shadeColor } from './night-color.js';

/**
 * Over every sprite of the world (their depth is their foot, 6 144 at most),
 * under the status line (10 000), the player's own bar (15 000) and the
 * damage numbers (20 000): the night never hides what a fight needs to read.
 */
const SHADE_DEPTH = 9_000;
/** The darkness only moves by steps (`DARKNESS_STEPS`): reading it once a second is enough. */
const READ_EVERY_MS = 1_000;
/** The camera may show past the map's edge (above the touch HUD): the shade spills over. */
const MARGIN_PX = 1_024;

/**
 * The night on the open world (ADR 0093): one flat rectangle over the whole
 * map, multiplied into the picture beneath. One colour, no gradient, no
 * blur (CLAUDE.md rule 6), and a single draw call: nothing a phone feels.
 * The time comes from the shared clock (`timeOfDay`), the same on the server;
 * this client only shows it and decides nothing.
 */
export class NightShade {
  private readonly shade: Phaser.GameObjects.Rectangle;
  private readonly unfollow: () => void;
  private enabled = true;
  private darkness = -1;
  private nextReadAt = 0;

  /** `width` and `height`: the map's size in pixels. */
  constructor(scene: Phaser.Scene, width: number, height: number) {
    this.shade = scene.add
      .rectangle(-MARGIN_PX, -MARGIN_PX, width + 2 * MARGIN_PX, height + 2 * MARGIN_PX, 0xffffff)
      .setOrigin(0, 0)
      .setDepth(SHADE_DEPTH)
      .setBlendMode(Phaser.BlendModes.MULTIPLY)
      .setVisible(false);
    this.unfollow = followPreference('nightAndWeather', (enabled) => {
      this.enabled = enabled;
      this.darkness = -1;
      this.nextReadAt = 0;
    });
  }

  /** Once a frame, `time` on the scene's clock. */
  update(time: number): void {
    if (time < this.nextReadAt) return;
    this.nextReadAt = time + READ_EVERY_MS;
    const darkness = this.enabled ? timeOfDay(Date.now()).darkness : 0;
    if (darkness === this.darkness) return;
    this.darkness = darkness;
    this.shade.setVisible(darkness > 0);
    if (darkness > 0) this.shade.setFillStyle(shadeColor(darkness));
  }

  destroy(): void {
    this.unfollow();
    this.shade.destroy();
  }
}
